/* eslint-disable ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/no-runloop, ember/require-tagless-components */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { computed } from '@ember/object';
import { later } from '@ember/runloop';
import { htmlSafe } from '@ember/template';
import confetti from 'canvas-confetti';

const RADIUS = 22.5;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export default class AvatarRing extends Component {
  // passed in
  sections = null; // activeReportContentSections

  @tracked sectionMap = null; // studentReportSectionMap[student.id]
  image = null;
  fullName = null;
  isComplete = false;
  circumference = CIRCUMFERENCE;

  @computed('elementId')
  get _ringId() {
    return `avatar-ring-${this.elementId}`;
  }

  _wasComplete = false;

  // Derive an observable array of Ember section models so @each.complete works
  @computed('sections.[]', 'sectionMap')
  get studentSections() {
    let sections = this.sections || [];
    let map = this.sectionMap || {};
    return sections.map((s) => map[s.id]).filter(Boolean);
  }

  @computed('studentSections.@each.complete')
  get completedCount() {
    return (this.studentSections || []).filter((s) => s.get('complete')).length;
  }

  @computed('sections.[]')
  get totalCount() {
    return (this.sections || []).length;
  }

  @computed('completedCount', 'totalCount')
  get isAllComplete() {
    let total = this.totalCount;
    return total > 0 && this.completedCount >= total;
  }

  didInsertElement() {
    super.didInsertElement(...arguments);
    this._wasComplete = this.isAllComplete;
  }

  didUpdate() {
    super.didUpdate(...arguments);
    let isComplete = this.isAllComplete;
    if (isComplete && !this._wasComplete) {
      this._fireConfetti();
    }
    this._wasComplete = isComplete;
  }

  _fireConfetti() {
    let el = document.getElementById(this._ringId);
    if (!el) return;

    // Clip confetti to the nearest sticky card header, falling back to the
    // ring element itself. The card header carries the Tailwind `sticky` class.
    let container = el.closest('.sticky') || el;
    let containerRect = container.getBoundingClientRect();
    let ringRect = el.getBoundingClientRect();

    // Create a temporary canvas fixed over the container so particles are
    // physically clipped to that area and never escape the card header.
    let canvas = document.createElement('canvas');
    canvas.width = Math.round(containerRect.width);
    canvas.height = Math.round(containerRect.height);
    Object.assign(canvas.style, {
      position: 'fixed',
      left: containerRect.left + 'px',
      top: containerRect.top + 'px',
      width: containerRect.width + 'px',
      height: containerRect.height + 'px',
      pointerEvents: 'none',
      zIndex: '9999',
    });
    document.body.appendChild(canvas);

    let myConfetti = confetti.create(canvas, { resize: false });

    // Origin is relative to the canvas bounds (0–1)
    let x =
      (ringRect.left + ringRect.width / 2 - containerRect.left) /
      containerRect.width;
    let y =
      (ringRect.top + ringRect.height / 2 - containerRect.top) /
      containerRect.height;

    myConfetti({
      particleCount: 40,
      spread: 20,
      origin: { x, y },
      startVelocity: 10,
      ticks: 80,
      scalar: 0.6,
      gravity: 1.2,
    });

    // Remove the canvas after the animation completes (ticks × ~16 ms + buffer)
    later(
      this,
      () => {
        if (canvas.parentNode) {
          canvas.parentNode.removeChild(canvas);
        }
      },
      2000,
    );
  }

  @computed('completedCount', 'totalCount')
  get dashOffset() {
    let total = this.totalCount;
    if (!total) return CIRCUMFERENCE;
    let progress = Math.min(this.completedCount / total, 1);
    return CIRCUMFERENCE * (1 - progress);
  }

  @computed('completedCount')
  get strokeColor() {
    return this.completedCount > 0 ? '#00A960' : '#E5E7EB';
  }

  ringStyle = htmlSafe(
    'transform: rotate(-90deg); transform-origin: 26px 26px; transition: stroke-dashoffset 0.4s, stroke 0.3s;',
  );
}
